import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { ApplicationStatus } from '../api/applicationService';
import { REACT_APP_ENVIRONMENT } from '../config';

/**
 * Utilidades para Sentry - Monitoreo de Errores, Rendimiento y Breadcrumbs
 */

// Constante para habilitar/deshabilitar logs de Sentry
const SENTRY_LOGS_ENABLED = false;

// Variable para rastrear si Sentry ha sido inicializado
let sentryInitialized = false;

/**
 * Función helper para logs condicionales de Sentry
 */
const sentryLog = (level: 'debug' | 'log' | 'info' | 'warn' | 'error', message: string, ...args: any[]) => {
  if (SENTRY_LOGS_ENABLED) {
    console[level](`[Sentry] ${message}`, ...args);
  }
};

/**
 * Verifica si Sentry está disponible y configurado
 */
const isSentryAvailable = (): boolean => {
  // Si ya sabemos que está inicializado, retornar true
  if (sentryInitialized) {
    return true;
  }

  try {
    // Verificar si Sentry está disponible como módulo importado
    if (typeof Sentry !== 'undefined' && typeof Sentry.captureMessage === 'function') {
      // Solo marcar como inicializado, NO enviar mensaje (evita traces innecesarios)
      sentryInitialized = true;
      return true;
    }
    return false;
  } catch (error) {
    sentryLog('debug', 'Sentry no está disponible:', error);
    return false;
  }
};

/**
 * Marcar Sentry como inicializado (llamado desde initSentry)
 */
export const markSentryInitialized = () => {
  sentryInitialized = true;
};

/**
 * Inicializa Sentry con configuración completa para OLE Web Application
 */
export const initSentry = (dsn: string) => {
  Sentry.init({
    dsn,
    // Configuraciones principales
    sendDefaultPii: true,
    environment: REACT_APP_ENVIRONMENT || 'development',
    // Habilitar sistema de Logs de Sentry (para ver console.log/warn/etc en dashboard)
    enableLogs: true,
    // Deshabilitar traces automáticos (solo errores generarán traces)
    tracesSampleRate: 0,
    // Configurar integraciones disponibles en Sentry v10.10.0
    integrations: [
      // Captura automática de console.log/warn/error/info/debug como LOGS (NO como eventos)
      Sentry.consoleLoggingIntegration({ levels: ['log', 'warn', 'error', 'info', 'debug'] }),
      // NOTA: browserTracingIntegration removido para evitar traces automáticos de navegación
      // Captura automática de errores globales
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
      // Captura de breadcrumbs de consola
      Sentry.breadcrumbsIntegration({
        console: true,
        dom: true,
        fetch: true,
        history: true,
        sentry: true,
        xhr: true,
      }),
    ],
    // Configurar releases para mejor tracking
    release: process.env.REACT_APP_VERSION || '1.0.0',
    // Configurar breadcrumbs para mejor debugging
    beforeSend(event, hint) {
      // Marcar Sentry como inicializado cuando se envía el primer evento
      if (!sentryInitialized) {
        markSentryInitialized();
      }
      // Agregar contexto adicional a los errores
      sentryLog('log', 'Error capturado por Sentry:', event.exception?.values?.[0]?.value);

      // Agregar información del quoteID desde query params
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const quoteId = urlParams.get('quoteID');

        if (quoteId) {
          event.user = {
            id: quoteId,
            username: `quote_${quoteId}`,
          };

          // Agregar contexto específico del quote
          event.tags = {
            ...event.tags,
            quote_id: quoteId,
          };
        }
      } catch (error) {
        console.warn('Error getting quote ID for Sentry:', error);
      }

      // Agregar contexto de la aplicación
      event.tags = {
        ...event.tags,
        app_version: process.env.REACT_APP_VERSION || '1.0.0',
        environment: process.env.NODE_ENV || 'development',
        user_agent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString(),
      };

      // Agregar contexto del navegador
      event.contexts = {
        ...event.contexts,
        browser: {
          name: navigator.userAgent,
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          online: navigator.onLine,
        },
        page: {
          url: window.location.href,
          referrer: document.referrer,
          title: document.title,
        },
        device: {
          screen_resolution: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          pixel_ratio: window.devicePixelRatio,
        },
      };

      // Agregar información de localStorage relevante (sin datos sensibles)
      try {
        const localStorageKeys = Object.keys(localStorage);
        event.contexts.storage = {
          keys_count: localStorageKeys.length,
          has_quote_data: localStorageKeys.some(key => key.includes('quote')),
          has_form_data: localStorageKeys.some(key => key.includes('form')),
        };
      } catch (error) {
        console.warn('Error accessing localStorage for Sentry context:', error);
      }

      // Agregar contexto de la sesión de navegación
      event.contexts.session = {
        session_start: new Date().toISOString(),
        page_load_time: performance.now(),
        navigation_type: performance.navigation.type,
      };

      return event;
    },
    // Capturar errores no manejados con mejor contexto
    beforeBreadcrumb(breadcrumb, hint) {
      // Personalizar breadcrumbs
      if (breadcrumb.category === 'console') {
        breadcrumb.message = `Console: ${breadcrumb.message}`;
      }

      // Agregar timestamp si no existe
      if (!breadcrumb.timestamp) {
        breadcrumb.timestamp = Date.now() / 1000;
      }

      // Agregar contexto adicional a breadcrumbs de navegación
      if (breadcrumb.category === 'navigation') {
        breadcrumb.data = {
          ...breadcrumb.data,
          user_agent: navigator.userAgent,
          referrer: document.referrer,
        };
      }

      // Agregar contexto a breadcrumbs de UI
      if (breadcrumb.category === 'ui.click' || breadcrumb.category === 'ui.input') {
        breadcrumb.data = {
          ...breadcrumb.data,
          url: window.location.href,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
        };
      }

      return breadcrumb;
    },
    // Configurar muestreo para diferentes tipos de errores
    sampleRate: 1.0, // Capturar todos los errores
    tracesSampler: (samplingContext) => {
      // Muestrear más transacciones de error
      if (samplingContext.request?.url?.includes('/api/')) {
        return 0.5; // 50% de las llamadas API
      }
      return 0.1; // 10% del resto
    },
  });
};

// ==================== 1. MONITOREO DE ERRORES ====================

/**
 * Captura un error personalizado con contexto adicional
 */
export const captureError = (
  error: Error | string,
  context?: Record<string, any>,
  tags?: Record<string, string>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureError');
    return;
  }

  Sentry.withScope((scope) => {
    // Agregar contexto adicional
    if (context) {
      scope.setContext('additional_info', context);
    }

    // Agregar tags para filtrado
    if (tags) {
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    // Agregar información del quote si existe
    const quoteInfo = getQuoteInfo();
    if (quoteInfo) {
      scope.setUser(quoteInfo);
    }

    Sentry.captureException(error instanceof Error ? error : new Error(error));
  });
};

/**
 * Captura errores de API con información detallada
 */
export const captureApiError = (
  error: any,
  endpoint: string,
  method: string = 'GET',
  requestData?: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureApiError');
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'api_error');
    scope.setTag('endpoint', endpoint);
    scope.setTag('method', method);

    scope.setContext('api_request', {
      endpoint,
      method,
      data: requestData,
      timestamp: new Date().toISOString()
    });

    if (error.response) {
      scope.setContext('api_response', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
    }

    Sentry.captureException(error);
  });
};

/**
 * Captura errores de React con información del componente
 */
export const captureReactError = (
  error: Error,
  errorInfo: any,
  componentName?: string
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureReactError');
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('error_type', 'react_error');
    if (componentName) {
      scope.setTag('component', componentName);
    }

    scope.setContext('react_error', {
      componentStack: errorInfo.componentStack,
      errorBoundary: true
    });

    Sentry.captureException(error);
  });
};

// ==================== 2. MONITOREO DE RENDIMIENTO ====================


/**
 * Monitorea el tiempo de ejecución de una función
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const measureFunctionPerformance = async <T>(
  functionName: string,
  fn: () => Promise<T> | T
): Promise<T> => {
  const startTime = performance.now();

  try {
    const result = await fn();
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `Function executed: ${functionName}`,
        'function_performance',
        'info',
        { functionName, duration, success: true, timestamp: new Date().toISOString() }
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `Function failed: ${functionName}`,
        'function_performance',
        'error',
        { functionName, duration, success: false, error: error instanceof Error ? error.message : String(error), timestamp: new Date().toISOString() }
      );
    }

    throw error;
  }
};

/**
 * Monitorea llamadas a API
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const measureApiCall = async <T>(
  endpoint: string,
  method: string,
  apiCall: () => Promise<T>
): Promise<T> => {
  const startTime = performance.now();

  try {
    const result = await apiCall();
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call: ${method} ${endpoint}`,
        'api_call',
        'info',
        { endpoint, method, duration, success: true, timestamp: new Date().toISOString() }
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call failed: ${method} ${endpoint}`,
        'api_call',
        'error',
        { endpoint, method, duration, success: false, error: error instanceof Error ? error.message : String(error), timestamp: new Date().toISOString() }
      );
    }

    throw error;
  }
};

// ==================== 3. BREADCRUMBS Y RASTREO DE SESIONES ====================

/**
 * Agrega un breadcrumb personalizado
 */
export const addBreadcrumb = (
  message: string,
  category: string = 'custom',
  level: 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo addBreadcrumb');
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000
  });
};

/**
 * Breadcrumb para acciones del usuario
 */
export const trackUserAction = (
  action: string,
  element?: string,
  additionalData?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackUserAction');
    return;
  }

  addBreadcrumb(
    `User action: ${action}`,
    'user_action',
    'info',
    {
      action,
      element,
      ...additionalData,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Breadcrumb para navegación
 */
export const trackNavigation = (
  from: string,
  to: string,
  method: 'push' | 'replace' | 'back' | 'forward' = 'push'
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackNavigation');
    return;
  }

  addBreadcrumb(
    `Navigation: ${from} → ${to}`,
    'navigation',
    'info',
    {
      from,
      to,
      method,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Breadcrumb para cambios de estado
 */
export const trackStateChange = (
  component: string,
  previousState: any,
  newState: any,
  action?: string
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackStateChange');
    return;
  }

  addBreadcrumb(
    `State change in ${component}`,
    'state_change',
    'info',
    {
      component,
      action,
      previousState: typeof previousState === 'object' ?
        JSON.stringify(previousState) : previousState,
      newState: typeof newState === 'object' ?
        JSON.stringify(newState) : newState,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Breadcrumb para eventos de formulario
 */
export const trackFormEvent = (
  formName: string,
  event: 'submit' | 'reset' | 'change' | 'focus' | 'blur',
  field?: string,
  value?: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormEvent');
    return;
  }

  addBreadcrumb(
    `Form ${event}: ${formName}`,
    'form_event',
    'info',
    {
      formName,
      event,
      field,
      value: typeof value === 'object' ? JSON.stringify(value) : value,
      timestamp: new Date().toISOString()
    }
  );
};

// ==================== UTILIDADES AUXILIARES ====================

/**
 * Obtiene información del quoteID desde query params
 */
const getQuoteInfo = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const quoteId = urlParams.get('quoteID');

    if (quoteId) {
      return {
        id: quoteId,
        username: `quote_${quoteId}`,
        type: 'quote_session'
      };
    }
  } catch (error) {
    console.warn('Error getting quote ID for Sentry:', error);
  }
  return null;
};

/**
 * Función helper para capturar mensajes con quoteID siempre al inicio
 */
export const captureMessageWithQuoteId = (message: string, options?: any) => {
  // Siempre obtener el quoteID desde los query params (siempre es 'quoteID')
  let quoteId = '';
  try {
    const urlParams = new URLSearchParams(window.location.search);
    quoteId = urlParams.get('quoteID') || '';
  } catch (error) {
    console.warn('Error getting quote ID for Sentry message:', error);
  }

  // Agregar el quoteId al inicio del mensaje (siempre, incluso si está vacío)
  const messageWithQuoteId = quoteId ? `[${quoteId}] ${message}` : `[NO_QUOTE_ID] ${message}`;

  return Sentry.captureMessage(messageWithQuoteId, options);
};

/**
 * Configura información del quote en Sentry
 */
export const setQuoteInfo = (quote: {
  id: string;
  type?: string;
  [key: string]: any;
}) => {
  const { id, ...otherProps } = quote;
  Sentry.setUser({
    id: id,
    username: `quote_${id}`,
    ...otherProps,
  });
};

/**
 * Configura información del usuario en Sentry (para casos especiales)
 */
export const setUserInfo = (user: {
  id?: string;
  email?: string;
  username?: string;
  [key: string]: any;
}) => {
  Sentry.setUser(user);
};

/**
 * Limpia información del usuario
 */
export const clearUserInfo = () => {
  Sentry.setUser(null);
};

/**
 * Agrega contexto global a Sentry
 */
export const setGlobalContext = (key: string, context: Record<string, any>) => {
  Sentry.setContext(key, context);
};

/**
 * Configura tags globales
 */
export const setGlobalTag = (key: string, value: string) => {
  Sentry.setTag(key, value);
};

/**
 * Captura errores con contexto completo de la aplicación OLE
 */
export const captureOLEError = (
  error: Error | string,
  context?: {
    component?: string;
    action?: string;
    page?: string;
    userId?: string;
    formData?: any;
    apiEndpoint?: string;
    additionalData?: Record<string, any>;
  }
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureOLEError');
    return;
  }

  Sentry.withScope((scope) => {
    // Tags específicos de OLE
    scope.setTag('app', 'ole-web-application');
    scope.setTag('component', context?.component || 'unknown');
    scope.setTag('page', context?.page || window.location.pathname);
    scope.setTag('action', context?.action || 'unknown');

    // Contexto de navegación
    scope.setContext('navigation', {
      url: window.location.href,
      referrer: document.referrer,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    });

    // Contexto de la aplicación OLE
    const quoteInfo = getQuoteInfo();
    scope.setContext('ole_app', {
      environment: process.env.NODE_ENV || 'development',
      version: process.env.REACT_APP_VERSION || '1.0.0',
      quoteId: quoteInfo?.id || context?.userId,
      sessionType: 'quote_session',
      urlParams: Object.fromEntries(new URLSearchParams(window.location.search)),
    });

    // Contexto específico del error
    if (context?.formData) {
      scope.setContext('form_data', {
        hasData: !!context.formData,
        fieldsCount: Object.keys(context.formData).length,
        // No incluir datos sensibles
      });
    }

    if (context?.apiEndpoint) {
      scope.setContext('api_call', {
        endpoint: context.apiEndpoint,
        method: 'POST', // Asumiendo POST para formularios
        timestamp: new Date().toISOString(),
      });
    }

    // Contexto adicional personalizado
    if (context?.additionalData) {
      scope.setContext('additional_context', context.additionalData);
    }

    // Información del quote si está disponible
    if (!scope.getUser()) {
      const quoteInfo = getQuoteInfo();
      if (quoteInfo) {
        scope.setUser(quoteInfo);
      }
    }

    // Capturar el error
    Sentry.captureException(error instanceof Error ? error : new Error(error));
  });
};

/**
 * Captura errores de formularios OLE específicamente
 */
export const captureFormError = (
  error: Error | string,
  formName: string,
  formData?: any,
  additionalContext?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureFormError');
    return;
  }

  captureOLEError(error, {
    component: `${formName}_form`,
    action: 'form_submission',
    formData,
    ...additionalContext,
  });
};

/**
 * Captura errores de navegación OLE
 */
export const captureNavigationError = (
  error: Error | string,
  from: string,
  to: string,
  additionalContext?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo captureNavigationError');
    return;
  }

  captureOLEError(error, {
    component: 'navigation',
    action: 'page_transition',
    page: to,
    additionalData: {
      navigation: { from, to },
      ...additionalContext,
    },
  });
};

// ==================== FUNCIONALIDADES AVANZADAS DE SENTRY ====================

/**
 * Monitoreo de Core Web Vitals - Métricas de rendimiento críticas
 */
export const measureCoreWebVitals = () => {
  // Web Vitals se capturan automáticamente con browserMetricsIntegration
  // Pero podemos agregar mediciones personalizadas
  if ('web-vitals' in window) {
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS((data) => sentryLog('log', 'Core Web Vital CLS:', data));
      getFID((data) => sentryLog('log', 'Core Web Vital FID:', data));
      getFCP((data) => sentryLog('log', 'Core Web Vital FCP:', data));
      getLCP((data) => sentryLog('log', 'Core Web Vital LCP:', data));
      getTTFB((data) => sentryLog('log', 'Core Web Vital TTFB:', data));
    });
  }
};

/**
 * Monitoreo de formularios críticos de seguros
 */
export const monitorInsuranceForm = (
  formId: string,
  formData: any,
  step: string
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo monitorInsuranceForm');
    return;
  }

  // Solo breadcrumb (NO genera trace)
  addBreadcrumb(
    `Insurance form step: ${step}`,
    'insurance_form',
    'info',
    {
      formId,
      step,
      hasData: !!formData,
      fieldsCount: Object.keys(formData || {}).length,
      quoteId: getQuoteInfo()?.id,
      timestamp: new Date().toISOString(),
    }
  );
};

/**
 * Monitoreo de cálculos de cotizaciones
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const monitorQuoteCalculation = async (
  calculationType: string,
  inputData: any,
  callback: () => Promise<any>
) => {
  const startTime = performance.now();

  // Breadcrumb al inicio
  if (isSentryAvailable()) {
    addBreadcrumb(
      `Quote calculation started: ${calculationType}`,
      'quote_calculation',
      'info',
      {
        calculationType,
        inputSize: JSON.stringify(inputData).length,
        quoteId: getQuoteInfo()?.id,
        timestamp: new Date().toISOString(),
      }
    );
  }

  try {
    const result = await callback();
    const duration = performance.now() - startTime;

    // Breadcrumb de éxito
    if (isSentryAvailable()) {
      addBreadcrumb(
        `Quote calculation completed: ${calculationType}`,
        'quote_calculation',
        'info',
        {
          calculationType,
          success: true,
          duration,
          timestamp: new Date().toISOString(),
        }
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    // Breadcrumb de error
    if (isSentryAvailable()) {
      addBreadcrumb(
        `Quote calculation failed: ${calculationType}`,
        'quote_calculation',
        'error',
        {
          calculationType,
          success: false,
          duration,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        }
      );
    }

    throw error;
  }
};

/**
 * Monitoreo de APIs críticas de seguros
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const monitorInsuranceAPI = async (
  endpoint: string,
  method: string,
  operation: string,
  callback: () => Promise<any>
) => {
  const startTime = performance.now();

  // Breadcrumb al inicio
  if (isSentryAvailable()) {
    addBreadcrumb(
      `API call started: ${operation}`,
      'insurance_api',
      'info',
      {
        endpoint,
        method,
        operation,
        quoteId: getQuoteInfo()?.id,
        timestamp: new Date().toISOString(),
      }
    );
  }

  try {
    const result = await callback();
    const duration = performance.now() - startTime;

    // Breadcrumb de éxito
    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call completed: ${operation}`,
        'insurance_api',
        'info',
        {
          endpoint,
          method,
          operation,
          success: true,
          duration,
          timestamp: new Date().toISOString(),
        }
      );
    }

    return result;
  } catch (error) {
    const duration = performance.now() - startTime;

    // Breadcrumb de error
    if (isSentryAvailable()) {
      addBreadcrumb(
        `API call failed: ${operation}`,
        'insurance_api',
        'error',
        {
          endpoint,
          method,
          operation,
          success: false,
          duration,
          error: error instanceof Error ? error.message : String(error),
          statusCode: (error as any)?.response?.status,
          timestamp: new Date().toISOString(),
        }
      );
    }

    throw error;
  }
};

/**
 * Configurar feedback de usuario para reportar problemas
 */
export const setupUserFeedback = () => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo setupUserFeedback');
    return;
  }

  // Configurar el widget de feedback de Sentry
  Sentry.showReportDialog({
    title: 'Reportar un Problema',
    subtitle: 'Ayúdanos a mejorar OLE reportando este problema.',
    subtitle2: 'Los datos sensibles serán protegidos.',
    labelName: 'Nombre',
    labelEmail: 'Email',
    labelComments: 'Describe el problema',
    labelSubmit: 'Enviar Reporte',
    successMessage: '¡Gracias! Tu reporte ha sido enviado.',
    errorGeneric: 'Ha ocurrido un error al enviar el reporte.',
    eventId: Sentry.lastEventId(),
  });
};

/**
 * Monitoreo de conversiones de seguros
 */
export const trackInsuranceConversion = (
  conversionType: 'quote_started' | 'quote_completed' | 'application_started' | 'application_completed' | 'policy_issued',
  data: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackInsuranceConversion');
    return;
  }

  // Solo breadcrumb (NO genera trace, solo contexto para errores)
  addBreadcrumb(
    `Conversion: ${conversionType}`,
    'insurance_conversion',
    'info',
    {
      conversionType,
      quoteId: getQuoteInfo()?.id,
      ...data,
      timestamp: new Date().toISOString(),
    }
  );
};

/**
 * Monitoreo de cambios de estado de aplicación por quoteID
 */
export const trackApplicationState = (
  quoteId: string,
  state: ApplicationStatus,
  previousState?: string,
  metadata?: Record<string, any>
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackApplicationState');
    return;
  }

  const stateData = {
    quoteId,
    currentState: state,
    previousState,
    stateChange: previousState ? `${previousState} → ${state}` : `initial: ${state}`,
    timestamp: new Date().toISOString(),
    ...metadata
  };

  // Solo breadcrumb (NO genera trace, solo contexto para errores)
  addBreadcrumb(
    `App state: ${quoteId} - ${previousState || 'none'} → ${state}`,
    'application_state',
    'info',
    stateData
  );
};

/**
 * Monitoreo de abandono de formularios
 */
export const trackFormAbandonment = (
  formId: string,
  step: string,
  timeSpent: number,
  data: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormAbandonment');
    return;
  }

  // Solo breadcrumb (NO genera trace, solo contexto para errores)
  addBreadcrumb(
    `Form abandonment: ${formId} at ${step}`,
    'form_abandonment',
    'warning',
    {
      formId,
      step,
      timeSpent,
      data,
      timestamp: new Date().toISOString(),
    }
  );
};

/**
 * Monitoreo de rendimiento de carga de página
 */
export const monitorPageLoadPerformance = () => {
  if ('performance' in window) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

        if (perfData) {
          // Métricas de rendimiento (disponibles en versiones más nuevas)
          // Sentry.metrics.distribution('page_load_time', perfData.loadEventEnd - perfData.loadEventStart, {
          //   unit: 'millisecond'
          // });
          //
          // Sentry.metrics.distribution('dom_content_loaded', perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart, {
          //   unit: 'millisecond'
          // });

          // Contexto adicional de rendimiento
          setGlobalContext('page_performance', {
            loadTime: perfData.loadEventEnd - perfData.loadEventStart,
            domContentLoaded: perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart,
            firstPaint: performance.getEntriesByName('first-paint')[0]?.startTime,
            firstContentfulPaint: performance.getEntriesByName('first-contentful-paint')[0]?.startTime,
          });
        }
      }, 0);
    });
  }
};

/**
 * Monitoreo de memoria y rendimiento del navegador
 */
export const monitorBrowserPerformance = () => {
  if ('memory' in performance) {
    setInterval(() => {
      const memInfo = (performance as any).memory;

      // Métricas de memoria (disponibles en versiones más nuevas)
      // Sentry.metrics.gauge('browser_memory_used', memInfo.usedJSHeapSize, {
      //   unit: 'byte'
      // });
      //
      // Sentry.metrics.gauge('browser_memory_total', memInfo.totalJSHeapSize, {
      //   unit: 'byte'
      // });
      //
      // Sentry.metrics.gauge('browser_memory_limit', memInfo.jsHeapSizeLimit, {
      //   unit: 'byte'
      // });

      // Enviar como contexto adicional
      setGlobalContext('browser_memory', {
        used: memInfo.usedJSHeapSize,
        total: memInfo.totalJSHeapSize,
        limit: memInfo.jsHeapSizeLimit,
        timestamp: new Date().toISOString(),
      });
    }, 30000); // Cada 30 segundos
  }
};

/**
 * Hook personalizado para monitoreo de formularios OLE
 */
export const useFormMonitoring = (formId: string) => {
  const [startTime] = useState(() => Date.now());
  const [currentStep, setCurrentStep] = useState(1);

  const trackStepChange = (newStep: number, formData?: any) => {
    const timeSpent = Date.now() - startTime;

    if (isSentryAvailable()) {
      monitorInsuranceForm(formId, formData, `step_${newStep}`);

      addBreadcrumb(
        `Form step changed: ${currentStep} → ${newStep}`,
        'form_navigation',
        'info',
        {
          formId,
          fromStep: currentStep,
          toStep: newStep,
          timeSpent,
          timestamp: new Date().toISOString()
        }
      );
    } else {
      sentryLog('debug', 'Sentry no está disponible, omitiendo trackStepChange');
    }

    setCurrentStep(newStep);
  };

  const trackFormAbandonment = (reason?: string) => {
    const timeSpent = Date.now() - startTime;

    if (isSentryAvailable()) {
      // Solo breadcrumb (NO genera trace, solo contexto para errores)
      addBreadcrumb(
        `Form abandonment: ${formId} at step ${currentStep}`,
        'form_abandonment',
        'warning',
        {
          formId,
          step: `step_${currentStep}`,
          timeSpent,
          reason,
          lastStep: currentStep,
          timestamp: new Date().toISOString()
        }
      );
    } else {
      sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormAbandonment');
    }
  };

  const trackFormCompletion = (completionData?: any) => {
    const timeSpent = Date.now() - startTime;

    if (isSentryAvailable()) {
      addBreadcrumb(
        `Form completed: ${formId}`,
        'form_completion',
        'info',
        {
          formId,
          totalSteps: currentStep,
          timeSpent,
          completionData,
          timestamp: new Date().toISOString()
        }
      );
    } else {
      sentryLog('debug', 'Sentry no está disponible, omitiendo trackFormCompletion');
    }
  };

  return {
    trackStepChange,
    trackFormAbandonment,
    trackFormCompletion,
    currentStep
  };
};

/**
 * Monitoreo específico para aplicaciones de seguros
 */
/**
 * Monitoreo específico para aplicaciones de seguros
 * NOTA: Solo usa breadcrumbs, NO genera traces
 */
export const monitorApplicationFlow = (
  applicationId: string,
  currentPage: number,
  totalPages: number,
  formData?: any
) => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo monitorApplicationFlow');
    return;
  }

  // Solo breadcrumb (NO genera trace)
  addBreadcrumb(
    `Application progress: ${currentPage}/${totalPages}`,
    'application_progress',
    'info',
    {
      applicationId,
      currentPage,
      totalPages,
      progress: (currentPage / totalPages) * 100,
      quoteId: getQuoteInfo()?.id,
      timestamp: new Date().toISOString()
    }
  );
};

/**
 * Configurar alertas inteligentes
 */
export const setupSmartAlerts = () => {
  if (!isSentryAvailable()) {
    sentryLog('debug', 'Sentry no está disponible, omitiendo setupSmartAlerts');
    return;
  }

  // Configurar alertas basadas en métricas
  // Esto se configura en el dashboard de Sentry, pero podemos documentar las métricas importantes

  const importantMetrics = [
    'quote_calculation_success',
    'quote_calculation_error',
    'insurance_api_success',
    'insurance_api_error',
    'form_abandonment',
    'insurance_conversion',
    'page_load_time',
    'browser_memory_used'
  ];

  sentryLog('log', 'Sentry metrics configured for OLE:', importantMetrics);
};

const sentryUtils = {
  initSentry,
  markSentryInitialized,
  captureError,
  captureApiError,
  captureReactError,
  captureOLEError,
  captureFormError,
  captureNavigationError,
  measureFunctionPerformance,
  measureApiCall,
  addBreadcrumb,
  trackUserAction,
  trackNavigation,
  trackStateChange,
  trackFormEvent,
  setQuoteInfo,
  setUserInfo,
  clearUserInfo,
  setGlobalContext,
  setGlobalTag,
  // Funcionalidades avanzadas
  measureCoreWebVitals,
  monitorInsuranceForm,
  monitorQuoteCalculation,
  monitorInsuranceAPI,
  setupUserFeedback,
  trackInsuranceConversion,
  trackApplicationState,
  trackFormAbandonment,
  monitorPageLoadPerformance,
  monitorBrowserPerformance,
  setupSmartAlerts,
  // Hooks y utilidades específicas de OLE
  useFormMonitoring,
  monitorApplicationFlow
};

export default sentryUtils;
